import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { RemovableBadge } from '@/components/ui/removable-badge';
import { cn } from '@/lib/utils';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Absent or empty renders a plain field; non-empty adds a suggestion listbox. */
  suggestions?: string[];
  placeholder?: string;
  /** Accessible name of the field, e.g. 「タグを追加」. */
  addLabel: string;
  /** Suffixed to each tag to name its remove control, e.g. 「削除」. */
  removeLabel: string;
  id?: string;
  className?: string;
  /**
   * Commit whatever is typed when the field loses focus. Off by default: MUI's
   * Autocomplete discards an unconfirmed draft on blur, and committing it means
   * a tag the user never confirmed reaches the submitted payload.
   */
  commitOnBlur?: boolean;
  /** Lets <Field> wire its help/error text to the input it renders. */
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const EMPTY: string[] = [];

const optionId = (listboxId: string, index: number) => `${listboxId}-option-${index}`;

const nextIndex = (current: number, step: number, count: number) => {
  if (current < 0) return step > 0 ? 0 : count - 1;
  return (current + step + count) % count;
};

const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  suggestions = EMPTY,
  placeholder,
  addLabel,
  removeLabel,
  id,
  className,
  commitOnBlur = false,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [draft, setDraft] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const listboxId = `${React.useId()}-tags`;

  const available = React.useMemo(() => {
    const needle = draft.trim().toLowerCase();
    return suggestions.filter(
      (item) => !value.includes(item) && item.toLowerCase().includes(needle),
    );
  }, [suggestions, value, draft]);

  const hasSuggestions = suggestions.length > 0;
  const isOpen = open && available.length > 0;

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || value.includes(tag)) return;
    onChange([...value, tag]);
    setDraft('');
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => nextIndex(current, step, available.length));
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    // isComposing keeps the Enter that confirms a kana→kanji conversion from
    // committing a half-typed tag.
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      const highlighted = isOpen && activeIndex >= 0 ? available[activeIndex] : undefined;
      // Only swallow Enter when it has something to add. With an empty draft it
      // must still reach the form, which is how MUI's Autocomplete behaved and
      // how a keyboard user submits from this field.
      if (!highlighted && !draft.trim()) return;
      event.preventDefault();
      addTag(highlighted ?? draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '') {
      onChange(value.slice(0, -1));
    }
  };

  const comboboxProps: React.ComponentProps<'input'> = hasSuggestions
    ? {
        role: 'combobox',
        autoComplete: 'off',
        'aria-autocomplete': 'list',
        'aria-expanded': isOpen,
        'aria-controls': listboxId,
        'aria-activedescendant':
          isOpen && activeIndex >= 0 ? optionId(listboxId, activeIndex) : undefined,
      }
    : {};

  const field = (
    <Input
      ref={inputRef}
      id={id}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        setActiveIndex(-1);
        setOpen(true);
      }}
      onKeyDown={handleKeyDown}
      onFocus={() => setOpen(true)}
      onBlur={() => {
        if (commitOnBlur) addTag(draft);
        setOpen(false);
      }}
      placeholder={placeholder}
      aria-label={addLabel}
      aria-describedby={describedBy}
      aria-invalid={invalid}
      {...comboboxProps}
    />
  );

  return (
    <div className={cn('space-y-3', className)}>
      {hasSuggestions ? (
        <Popover open={isOpen} onOpenChange={setOpen}>
          <PopoverAnchor asChild>{field}</PopoverAnchor>
          <PopoverContent
            align="start"
            sideOffset={6}
            onOpenAutoFocus={(event) => event.preventDefault()}
            // The input is the anchor, which lives OUTSIDE the content, so Radix
            // reads a press on it as an outside interaction and dismisses — the
            // same click that focuses the field would close the list again.
            onPointerDownOutside={(event) => {
              if (inputRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            onFocusOutside={(event) => {
              if (inputRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            className="revy-ui w-[var(--radix-popover-trigger-width)] p-0"
          >
            <div
              id={listboxId}
              role="listbox"
              className="max-h-60 overflow-y-auto p-1"
              // Keeping focus in the field stops the blur commit from adding the
              // half-typed draft alongside the suggestion being clicked.
              onMouseDown={(event) => event.preventDefault()}
            >
              {available.map((suggestion, index) => (
                <button
                  key={suggestion}
                  id={optionId(listboxId, index)}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  onClick={() => addTag(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={cn(
                    'revy-cjk block w-full rounded-md px-2 py-1.5 text-left text-sm leading-[1.5] transition-colors',
                    index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent',
                  )}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        field
      )}

      {/*
        flex-wrap only wraps BETWEEN chips — a single tag longer than the
        container cannot wrap and used to run past the dialog edge. `min-w-0`
        lets the item shrink below its content width so the label can truncate;
        the remove button stays reachable, and `title` keeps the full value.
      */}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <li key={tag} className="min-w-0 max-w-full">
              <RemovableBadge
                className="max-w-full"
                onRemove={() => onChange(value.filter((item) => item !== tag))}
                removeLabel={`${tag} — ${removeLabel}`}
              >
                <span className="revy-cjk min-w-0 truncate" title={tag}>{tag}</span>
              </RemovableBadge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export { TagInput };
